import { Teams } from '@watershed/constants/teams';
import Redirect from '@watershed/shared-frontend/components/Redirect';
import useLocationHash from '@watershed/shared-frontend/hooks/useLocationHash';
import { useOwner } from '@watershed/shared-frontend/hooks/useOwner';
import {
  routeForDocs,
  routeForDocsCategory,
  routeForDocsPage,
  routeForDocsSearch,
} from '@watershed/shared-universal/docsRoutes';

import { getDocsRedirectForHelpjuiceHash } from '../../utils/learningHubHelpjuiceRedirects';

/**
 * Builds the redirect URL from a DocsRedirectResult.
 */
function buildDocsRedirectUrl(
  result: ReturnType<typeof getDocsRedirectForHelpjuiceHash>
): string {
  switch (result.type) {
    case 'page': {
      const basePath = routeForDocsPage(result.slug);
      return result.anchor ? `${basePath}#${result.anchor}` : basePath;
    }
    case 'category':
      return routeForDocsCategory(result.slug);
    case 'search':
      return routeForDocsSearch(result.query);
    case 'home':
      return routeForDocs();
  }
}

/**
 * Learning Hub page that redirects to new /docs pages when feature flag is enabled.
 *
 * When LearningHubNewDocsUrls flag is enabled:
 * - Redirects old /learn#slug URLs to new /docs/slug URLs
 * - Uses mappings from utils/learningHubHelpjuiceRedirects.ts
 *
 * When flag is disabled:
 * - Shows the old Helpjuice embed for regular users
 * - Redirects full-screen users to /learn/full
 */
export default function Learn() {
  useOwner(Teams.ApplicationPlatform);
  const hash = useLocationHash();

  const redirectResult = getDocsRedirectForHelpjuiceHash(hash);
  const redirectUrl = buildDocsRedirectUrl(redirectResult);
  return (
    <Redirect
      to={redirectUrl}
      query={new URLSearchParams(window.location.search)}
    />
  );
}
